import React, { useEffect, useMemo, useState } from 'react'
import { getMessage } from '../../../utils/index'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { icons } from '../../../constants'
import apiService from '../../../services/api.service'
import LtsInputText from '../../../components/lottus/lts-input-text'
import LtsJsonArea from '../../../components/lottus/lts-json-area'
import { renderDefinicaoVisualForm, validateDefinicaoVisual } from './definicao-visual-form'

// o BE persiste a definição como texto; o LtsJsonArea trabalha melhor com objeto
// (pretty print no textarea e preview no dialog de edição visual)
const parseDefinicao = (definicao) => {
  if (definicao && typeof definicao === 'string') {
    try {
      return JSON.parse(definicao)
    } catch (e) {
      return definicao
    }
  }
  return definicao
}

// "data_nascimento" -> "Data Nascimento" (sugestão inicial; usuário ajusta depois)
const labelFromNome = (nome) =>
  String(nome || '')
    .split('_')
    .filter(Boolean)
    .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
    .join(' ')

// colunas de auditoria: entram na lista mas desmarcadas por padrão (preenchidas pelo sistema);
// oid_unidade vem marcada — desmarcar deixa valendo só o escopo implícito por unidade
const COLUNAS_DESMARCADAS = ['dt_gravacao', 'usuario_gravacao']

export const CrudDinamicoDefinicaoForm = (props) => {
  const record = props.record
  const messages = props.messages

  // memoizado: objeto novo a cada render mudaria a identidade do value do LtsJsonArea
  // e o effect interno resetaria o textarea a cada tecla digitada
  const definicaoValue = useMemo(() => parseDefinicao(record.definicao), [record.definicao])

  const [showDialogColunas, setShowDialogColunas] = useState(false)
  const [colunas, setColunas] = useState([])
  const [selecionadas, setSelecionadas] = useState({})
  const [exibicaoLookup, setExibicaoLookup] = useState({})
  const [camposAtuais, setCamposAtuais] = useState({})
  const [erroObter, setErroObter] = useState(null)
  const [menusRaiz, setMenusRaiz] = useState([])

  // menu pai do menu criado automaticamente — só tem efeito em definição nova
  useEffect(() => {
    apiService
      .get('/lottustech/crud-dinamico-definicao/menus-raiz')
      .then(({ data }) => setMenusRaiz(data || []))
      .catch(() => setMenusRaiz([]))
  }, [])

  useEffect(() => {
    if (!record.oid && !record.oidMenuPai && menusRaiz.length) {
      // o nome vem com o caminho completo + oid: raiz "Cadastros" chega como "Cadastros (n)"
      const cadastros = menusRaiz.find((menu) => /^Cadastros \(\d+\)$/.test(menu.nome))
      if (cadastros) {
        props.setField('oidMenuPai', cadastros.oid)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.oid, record.oidMenuPai, menusRaiz])

  const obterDefinicao = () => {
    setErroObter(null)
    apiService
      .get(`/lottustech/crud-dinamico-definicao/colunas/${record.tabela}`)
      .then(({ data }) => {
        // merge com a definição existente: campos já declarados mantêm sua configuração;
        // colunas novas entram com o default; campos cuja coluna sumiu do banco aparecem
        // como órfãos (desmarcados, desabilitados) e saem do JSON ao confirmar
        const atuais = {}
        const definicaoAtual = definicaoValue && typeof definicaoValue === 'object' ? definicaoValue : null
        ;((definicaoAtual && definicaoAtual.campos) || []).forEach((campo) => {
          atuais[campo.nome] = campo
        })

        const marcadas = {}
        const exibicoes = {}
        data.forEach((coluna) => {
          const atual = atuais[coluna.nome]
          if (atual) {
            marcadas[coluna.nome] = { listagem: Boolean(atual.listagem), formulario: atual.formulario !== false }
          } else {
            const incluir = coluna.suportado && !COLUNAS_DESMARCADAS.includes(coluna.nome)
            marcadas[coluna.nome] = { listagem: incluir, formulario: incluir }
          }
          if (coluna.lookup) {
            exibicoes[coluna.nome] =
              (atual && atual.lookup && atual.lookup.campoExibicao) || coluna.lookup.campoExibicaoSugerido
          }
        })

        const nomesTabela = new Set(data.map((coluna) => coluna.nome))
        const orfaos = Object.keys(atuais)
          .filter((nome) => !nomesTabela.has(nome))
          .map((nome) => ({ nome, tipo: atuais[nome].tipo, suportado: false, orfao: true }))
        orfaos.forEach((coluna) => {
          marcadas[coluna.nome] = { listagem: false, formulario: false }
        })

        setColunas([...data, ...orfaos])
        setSelecionadas(marcadas)
        setExibicaoLookup(exibicoes)
        setCamposAtuais(atuais)
        setShowDialogColunas(true)
        // sugestões a partir da tabela quando ainda não preenchidos
        if (!record.chave) {
          props.setField('chave', record.tabela.replace(/_/g, '-'))
        }
        if (!record.nome) {
          props.setField('nome', labelFromNome(record.tabela))
        }
      })
      .catch((err) => {
        // 400 do BE traz a mensagem de negócio (tabela inexistente/proibida) no body
        const mensagem = err && err.response && err.response.data
        setErroObter(typeof mensagem === 'string' && mensagem ? mensagem : 'Não foi possível obter as colunas da tabela')
      })
  }

  const toggleColuna = (nome, destino, checked) => {
    setSelecionadas((atual) => ({ ...atual, [nome]: { ...atual[nome], [destino]: checked } }))
  }

  const colunaSelecionada = (coluna) => {
    const selecao = selecionadas[coluna.nome]
    return coluna.suportado && selecao && (selecao.listagem || selecao.formulario)
  }

  // "oid_grupo_financeiro" -> label "Grupo Financeiro" (sem o prefixo da FK)
  const labelFromColuna = (coluna) => labelFromNome(coluna.lookup ? coluna.nome.replace(/^oid_/, '') : coluna.nome)

  const gerarCampoNovo = (coluna, selecao) => {
    const campo = { nome: coluna.nome, tipo: coluna.tipo, label: labelFromColuna(coluna) }
    if (coluna.tipo === 'string' && coluna.tamanho) {
      campo.tamanho = coluna.tamanho
    }
    if (coluna.obrigatorio) {
      campo.obrigatorio = true
    }
    if (coluna.lookup) {
      campo.lookup = {
        chave: coluna.lookup.chave,
        tabela: coluna.lookup.tabela,
        pk: coluna.lookup.pk,
        campoExibicao: exibicaoLookup[coluna.nome] || coluna.lookup.campoExibicaoSugerido,
      }
    }
    campo.listagem = Boolean(selecao.listagem)
    campo.filtro = Boolean(selecao.listagem)
    campo.ordenavel = Boolean(selecao.listagem)
    // obrigatório precisa estar no formulário (regra do parser do BE) — força a inclusão
    if (!selecao.formulario && !coluna.obrigatorio) {
      campo.formulario = false
    }
    return campo
  }

  // campo já declarado preserva label/flags/limites; só os checkboxes e o campo de exibição mexem
  const gerarCampoExistente = (coluna, selecao) => {
    const atual = camposAtuais[coluna.nome]
    const campo = { ...atual }
    campo.listagem = Boolean(selecao.listagem)
    if (!selecao.listagem) {
      campo.filtro = false
      campo.ordenavel = false
    } else if (!atual.listagem) {
      campo.filtro = true
      campo.ordenavel = true
    }
    if (!selecao.formulario && !coluna.obrigatorio) {
      campo.formulario = false
    } else {
      delete campo.formulario
    }
    if (campo.lookup && coluna.lookup) {
      campo.lookup = { ...campo.lookup, campoExibicao: exibicaoLookup[coluna.nome] || campo.lookup.campoExibicao }
    }
    return campo
  }

  const gerarDefinicao = () => {
    const camposSelecionados = colunas.filter(colunaSelecionada)
    const campos = camposSelecionados.map((coluna) => {
      const selecao = selecionadas[coluna.nome]
      return camposAtuais[coluna.nome] ? gerarCampoExistente(coluna, selecao) : gerarCampoNovo(coluna, selecao)
    })

    const tituloForm = record.nome || labelFromNome(record.tabela)
    const primeiroString = camposSelecionados.find(
      (coluna) => coluna.tipo === 'string' && selecionadas[coluna.nome].listagem
    )
    const definicaoAtual =
      definicaoValue && typeof definicaoValue === 'object' && Object.keys(camposAtuais).length ? definicaoValue : null

    // definição existente preserva títulos/seleção múltipla/demais propriedades do topo
    const definicao = definicaoAtual
      ? { ...definicaoAtual, tabela: record.tabela, campos }
      : {
          tabela: record.tabela,
          tituloForm,
          tituloListagem: `${tituloForm}s`,
          ...(primeiroString ? { ordenacaoPadrao: [{ campo: primeiroString.nome, ordem: 'ASC' }] } : {}),
          campos,
        }
    // ordenação apontando para campo que saiu da definição quebraria o parser do BE
    if (definicao.ordenacaoPadrao) {
      definicao.ordenacaoPadrao = definicao.ordenacaoPadrao.filter((ordenacao) =>
        campos.some((campo) => campo.nome === ordenacao.campo)
      )
      if (!definicao.ordenacaoPadrao.length) {
        delete definicao.ordenacaoPadrao
      }
    }
    props.setField('definicao', definicao)
    setShowDialogColunas(false)
  }

  const renderDialogColunas = () => (
    <Dialog
      header="Colunas da tabela"
      visible={showDialogColunas}
      style={{ width: '40vw' }}
      onHide={() => setShowDialogColunas(false)}
      baseZIndex={1000000}
      modal
      closable
    >
      <p>Marque onde cada coluna aparece (Listagem e/ou Formulário; desmarcadas ficam fora do CRUD):</p>
      {colunas.map((coluna) => {
        const selecao = selecionadas[coluna.nome] || {}
        return (
          <div
            key={coluna.nome}
            className="p-col-12"
            style={{ padding: '4px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <span style={{ flex: '1 1 40%', minWidth: '200px' }}>
              {coluna.nome}{' '}
              <small style={{ color: '#888' }}>
                ({coluna.lookup ? `lookup → ${coluna.lookup.tabela}` : coluna.tipo || coluna.dataType})
              </small>
              {coluna.orfao && (
                <small style={{ color: '#c00' }}> — coluna não existe mais na tabela; sai do JSON ao confirmar</small>
              )}
              {!coluna.suportado && !coluna.orfao && <small style={{ color: '#c00' }}> — tipo não suportado</small>}
              {coluna.obrigatorio && <small style={{ color: '#888' }}> — obrigatório</small>}
            </span>
            <span style={{ flex: '0 0 110px' }}>
              <Checkbox
                inputId={`chkListagem_${coluna.nome}`}
                checked={Boolean(selecao.listagem)}
                disabled={!coluna.suportado}
                onChange={(e) => toggleColuna(coluna.nome, 'listagem', e.checked)}
                style={{ marginRight: '5px' }}
              />
              <label htmlFor={`chkListagem_${coluna.nome}`} className="p-checkbox-label">
                Listagem
              </label>
            </span>
            <span style={{ flex: '0 0 120px' }}>
              <Checkbox
                inputId={`chkForm_${coluna.nome}`}
                checked={Boolean(selecao.formulario)}
                disabled={!coluna.suportado}
                onChange={(e) => toggleColuna(coluna.nome, 'formulario', e.checked)}
                style={{ marginRight: '5px' }}
              />
              <label htmlFor={`chkForm_${coluna.nome}`} className="p-checkbox-label">
                Formulário
              </label>
            </span>
            {/* slot fixo mesmo sem lookup, para os checkboxes alinharem entre as linhas */}
            <span style={{ flex: '0 0 180px' }}>
              {coluna.lookup && (
                <Dropdown
                  value={exibicaoLookup[coluna.nome]}
                  options={coluna.lookup.camposDisponiveis.map((campo) => ({ label: campo, value: campo }))}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => setExibicaoLookup((atual) => ({ ...atual, [coluna.nome]: e.value }))}
                  placeholder="Campo de exibição"
                  style={{ width: '100%' }}
                />
              )}
            </span>
          </div>
        )
      })}
      <div style={{ marginTop: '20px' }} className="p-grid form-group">
        <div className="p-col-12" style={{ textAlign: 'right' }}>
          <Button
            label="Confirmar"
            icon={icons.CHECK_ICON}
            className="p-button-success"
            style={{ width: 'auto' }}
            onClick={gerarDefinicao}
          />
        </div>
      </div>
    </Dialog>
  )

  return (
    <div className="p-grid p-fluid">
      <div className="p-col-12">
        <Card title={getMessage(messages, 'crudDinamico.label.definicao') + ' ' + (record.oid || '')}>
          <div className="p-grid form-group">
            <div className="p-col-12 p-sm-8 p-md-6 p-lg-4 p-xl-4">
              <LtsInputText
                value={record.tabela}
                onChange={(e) => props.setField('tabela', e)}
                name={'tabela'}
                label={'Tabela'}
              />
            </div>
            <div className="p-col-12 p-sm-4 p-md-4 p-lg-3 p-xl-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                label="Obter Definição"
                icon="pi pi-search"
                className="p-button-outlined"
                disabled={!record.tabela}
                onClick={obterDefinicao}
              />
            </div>
            {erroObter && (
              <div className="p-col-12">
                <small className="p-error">{erroObter}</small>
              </div>
            )}
          </div>
          <div className="p-grid form-group">
            <div className="p-col-12 p-sm-8 p-md-6 p-lg-4 p-xl-4">
              <LtsInputText
                value={record.chave}
                onChange={(e) => props.setField('chave', e)}
                name={'chave'}
                label={'Chave de acesso (URL)'}
                tooltip={
                  'Identificador único deste CRUD, usado na URL de acesso à tela: /geral/crud/<chave>. ' +
                  'Use letras minúsculas e hífens (ex.: tipo-atendimento). Não altere após publicar o menu.'
                }
                tooltipOptions={{ position: 'top' }}
              />
            </div>
          </div>
          <div className="p-grid form-group">
            <div className="p-col-12 p-sm-8 p-md-6 p-lg-4 p-xl-4">
              <LtsInputText
                value={record.nome}
                onChange={(e) => props.setField('nome', e)}
                name={'nome'}
                label={'Nome'}
              />
              <small style={{ color: '#888' }}>Nome usado para criar a descrição do menu</small>
            </div>
          </div>
          {!record.oid && (
            <div className="p-grid form-group">
              <div className="p-col-12 p-sm-8 p-md-6 p-lg-4 p-xl-4">
                <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                  Menu pai
                </label>
                <Dropdown
                  value={record.oidMenuPai}
                  options={menusRaiz.map((menu) => ({ label: menu.nome, value: menu.oid }))}
                  optionLabel="label"
                  optionValue="value"
                  filter
                  filterBy="label"
                  filterPlaceholder="Pesquisar menu"
                  onChange={(e) => props.setField('oidMenuPai', e.value)}
                  placeholder="Menu pai"
                  style={{ width: '100%' }}
                />
                <small style={{ color: '#888' }}>
                  Menu raiz onde a tela será criada (vale só na criação; depois, mover pela tela de menus)
                </small>
              </div>
            </div>
          )}
          <div className="p-grid form-group">
            <div className="p-col-12">
              <Checkbox
                inputId="chkAtivo"
                checked={Boolean(record.ativo)}
                onChange={(e) => props.setField('ativo', e.checked)}
                style={{ marginRight: '5px' }}
              />
              <label htmlFor="chkAtivo" className="p-checkbox-label">
                Ativo
              </label>
            </div>
          </div>
          <div className="p-grid form-group">
            <div className="p-col-12 p-lg-8">
              <LtsJsonArea
                value={definicaoValue}
                onChange={(value) => props.setField('definicao', value)}
                name="definicao"
                rows={25}
                label={'Definição (JSON)'}
                modalTitle="Edição Visual da Definição"
                modalWidth="70vw"
                renderForm={renderDefinicaoVisualForm}
                validate={validateDefinicaoVisual}
              />
            </div>
          </div>
          {renderDialogColunas()}
        </Card>
      </div>
    </div>
  )
}
